/**
 * Client-side pricing rollup (mirrors backend/quotes/quoteTotals.js).
 * Update both if the model changes.
 */

export function applyCustomerRounding(exact, mode) {
    const x = Number(exact);
    if (!Number.isFinite(x)) return 0;
    const m = String(mode || "none").toLowerCase();
    if (m === "none" || m === "exact") return Math.round(x * 100) / 100;
    const map = {
        nearest_1: 1,
        nearest_10: 10,
        nearest_50: 50,
        nearest_100: 100,
        nearest_250: 250,
        nearest_500: 500,
        nearest_1000: 1000,
    };
    const inc = map[m] ?? 100;
    return Math.round(x / inc) * inc;
}

export function lineExtension(line) {
    const qtyN = Number(line?.qty);
    const rateN = Number(line?.bill_rate);
    const qty = Number.isFinite(qtyN) ? qtyN : 0;
    const rate = Number.isFinite(rateN) ? rateN : 0;
    return qty * rate;
}

export function computeQuoteTotals({ material_cost_at_purchase, lines, settings }) {
    const s = settings || {};
    const matCost = Math.max(0, Number(material_cost_at_purchase) || 0);
    const matMarkup = Number(s.material_markup_percent);
    const cont = Number(s.contingency_percent);
    const tax = Number(s.tax_rate_percent);
    const rounding = s.customer_total_rounding || "nearest_100";

    const matMarkupPct = Number.isFinite(matMarkup) ? matMarkup : 12;
    const contPct = Number.isFinite(cont) ? cont : 8;
    const taxPct = Number.isFinite(tax) ? tax : 0;

    const services_subtotal = (lines || []).reduce((sum, L) => sum + lineExtension(L), 0);
    const materials_customer = matCost * (1 + matMarkupPct / 100);
    const pre_risk_subtotal = services_subtotal + materials_customer;
    const contingency_amount = pre_risk_subtotal * (contPct / 100);
    const subtotal_after_cont = pre_risk_subtotal + contingency_amount;
    const tax_amount = subtotal_after_cont * (taxPct / 100);
    const customer_total_exact = subtotal_after_cont + tax_amount;
    const customer_total_rounded = applyCustomerRounding(customer_total_exact, rounding);

    return {
        services_subtotal,
        materials_cost_basis: matCost,
        materials_markup_percent: matMarkupPct,
        materials_customer,
        pre_risk_subtotal,
        contingency_percent: contPct,
        contingency_amount,
        tax_percent: taxPct,
        tax_amount,
        customer_total_exact,
        customer_total_rounded,
        rounding_mode: rounding,
    };
}

export function formatMoney(amount, currency = "USD") {
    try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 0 }).format(
            Number(amount) || 0
        );
    } catch {
        return `$${Math.round(Number(amount) || 0).toLocaleString()}`;
    }
}

export function newEstimateLineId() {
    try {
        const c = typeof window !== "undefined" ? window.crypto : undefined;
        if (c?.randomUUID) return c.randomUUID();
    } catch {
        /* ignore */
    }
    return `eln_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}
